import { ReactNode } from 'react';

import { Box, Button, Flex, FlexProps, Heading } from '@chakra-ui/react';

import { ClampedTextV2 } from '~/components/clamped-text-v2';
import { useI18n } from '~/hooks/use-i18n';

export type ItemCardProps = {
  imageSlot?: ReactNode;
  imageHeight?: FlexProps['height'];
  title?: ReactNode;
  summary?: ReactNode;
  buttonSlot?: ReactNode;
  color?: string;
  containerProps?: FlexProps;
  summaryLinesShown?: number; // overwrite the number of lines to show when the summary is truncated (default is 4)
};

const DEFAULT_NUM_SUMMARY_LINES_SHOWN = 4;

export function ItemCard({
  imageSlot,
  title,
  summary,
  buttonSlot,
  color,
  containerProps,
  imageHeight = '200px',
  summaryLinesShown = DEFAULT_NUM_SUMMARY_LINES_SHOWN,
}: ItemCardProps) {
  const { t } = useI18n();

  return (
    <Flex
      as='article'
      flexDirection='column'
      gap={6}
      position='relative'
      {...containerProps}
      sx={{
        ...containerProps?.sx,
        containerType: 'inline-size',
      }}
    >
      <Box
        borderRadius='8px'
        height={imageHeight}
        overflow='hidden'
        position='relative'
        width='100%'
        sx={{
          '@container (max-width: 33rem)': {
            height: '140px',
          },
        }}
      >
        {imageSlot}
      </Box>
      <Flex color={color} flexDirection='column' gap={6}>
        <Flex flexDirection='column' gap={2}>
          {!!title && typeof title === 'string' && (
            <Box
              flexBasis={{
                md: '72px',
              }}
            >
              <Heading
                size={{
                  base: 'marketing-xl',
                  md: 'marketing-2xl',
                }}
              >
                {title}
              </Heading>
            </Box>
          )}
          {!!summary && typeof summary === 'string' && (
            <Box
              flexBasis={{
                md: '104px',
              }}
            >
              <ClampedTextV2
                noOfLines={summaryLinesShown}
                renderButton={({ isClamped, onToggle }) => {
                  return (
                    <Button marginTop={2} paddingStart={0} variant='link' onClick={onToggle}>
                      {isClamped ? t('common.seeMore') : t('common.seeLess')}
                    </Button>
                  );
                }}
              >
                {summary}
              </ClampedTextV2>
            </Box>
          )}
          {!!title && typeof title === 'object' && title}
          {!!summary && typeof summary === 'object' && summary}
        </Flex>
        {buttonSlot}
      </Flex>
    </Flex>
  );
}
